// @ts-nocheck - Los errores de JSX.IntrinsicElements son falsos positivos del linter
// El código compila y funciona correctamente con React 18
import React, { useState } from 'react';
import './infoProcesos.css';


// Constantes de configuración
const GROUP_ORDER: string[] = [
  "Ventas",
  "Compras",
  "Insumos",
  "Nomina",
  "Administrativo",
  "Otros"
];

const CATEGORY_ORDER: { [key: string]: string[] } = {
  "Compras": [
    "Costo de ganado en pie",
    "Cuota de fomento",
    "Garantia en ganado",
    "Utilidad en finca - ptz -vt",
    "Comisiones"
  ],
  "Insumos": [
    "Sal [ ] Medicamentos",
    "Combustible",
    "Maquinaria Pesada",
    "Construcciones",
    "Herbicidas",
    "Herramientas",
    "Transporte"
  ],
  "Nomina": [
    "Gastos de Personal",
    "AIGC"
  ],
  "Otros": [
    "Otros"
  ]
};

// Interfaces
interface FinancialData {
  fecha_str: string;
  fecha: string;
  total_ingresos: number;
  total_costos: number;
  total_gastos: number;
  grupos: {
    [key: string]: {
      valor: number;
      subcategorias: {
        [key: string]: {
          valor: number;
          detalles?: Array<{
            codigo: string;
            descripcion: string;
            valor: number;
          }>;
        };
      };
    };
  };
}

interface InfoProcesosProps {
  data: FinancialData | null;
  loading?: boolean;
  error?: string | null;
}

const InfoProcesos: React.FC<InfoProcesosProps> = ({ data, loading = false, error = null }) => {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  // Función para formatear números
  const formatNumber = (number: number): string => {
    return new Intl.NumberFormat('es-CO', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(number);
  };

  // Función para obtener categorías ordenadas
  const getCategoriesByGroup = (group: string): Array<[string, any]> => {
    if (!data?.grupos?.[group]?.subcategorias) {
      return [];
    }
    
    const categories: Array<[string, any]> = Object.entries(
      data.grupos[group].subcategorias
    ).map(([name, data]) => [name, data] as [string, any]);
    
    if (CATEGORY_ORDER[group]) {
      return categories.sort(([nameA], [nameB]) => {
        const indexA = CATEGORY_ORDER[group].indexOf(nameA);
        const indexB = CATEGORY_ORDER[group].indexOf(nameB);
        
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        } else if (indexA !== -1) {
          return -1;
        } else if (indexB !== -1) {
          return 1;
        }
        return 0;
      });
    }
    
    return categories;
  };

  // Función para manejar expansión/colapso
  const handleGroupToggle = (group: string): void => {
    setExpandedGroup(expandedGroup === group ? null : group);
  };

  // Renderizar detalles de "Otros"
  const renderOtrosDetails = (categoryData: any): React.ReactElement[] | null => {
    if (!categoryData || !categoryData.detalles) {
      return null;
    }

    return categoryData.detalles.map((detalle: any, index: number) => (
      <div key={`${detalle.codigo}-${index}`} className="info-procesos-subcategory-item otros-detail">
        <div className="info-procesos-subcategory-name">
          {detalle.descripcion} <span className="code-badge">({detalle.codigo})</span>
        </div>
        <div className="info-procesos-subcategory-value">
          <span className="currency-symbol">$</span>
          <span className="value-text">{formatNumber(detalle.valor)}</span>
        </div>
      </div>
    ));
  };

  // Renderizar grupo
  const renderGroup = (groupName: string, groupData: any): React.ReactElement => {
    const categories = getCategoriesByGroup(groupName);
    const isExpanded = expandedGroup === groupName;
    const hasAtLeastOneCategory = categories.length > 0;
    
    // Para "Otros", verificar si tiene detalles (códigos) para hacerlo expandable
    const hasOtrosDetails = groupName === "Otros" && categories.some(([_, category]) => 
      category?.detalles && Array.isArray(category.detalles) && category.detalles.length > 0
    );
    
    const isExpandable = hasAtLeastOneCategory || hasOtrosDetails;
    return (
      <div 
        key={groupName} 
        className={`info-procesos-card ${isExpanded ? 'expanded' : ''} ${isExpandable ? 'expandable' : 'single'}`}
      >
        <div 
          className="info-procesos-card-header"
          onClick={() => isExpandable && handleGroupToggle(groupName)}
          style={{ cursor: isExpandable ? 'pointer' : 'default' }}
        >
          <div className="info-procesos-card-title">
            <span className="group-name">{groupName}</span>
            {isExpandable && (
              <span className="expand-indicator">
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
          </div>
          <div className="info-procesos-card-value">
            <span className="currency-symbol">$</span>
            <span className="value-text">{formatNumber(groupData.valor)}</span>
          </div>
        </div>

        {isExpandable && isExpanded && (
          <div className="info-procesos-card-body">
            {groupName === "Otros" ? (
              // Renderizado especial para "Otros"
              categories.flatMap(([categoryName, category]) => 
                renderOtrosDetails(category) || []
              )
            ) : (
              // Renderizado normal de subcategorías
              categories.map(([categoryName, category]) => (
                <div key={categoryName} className="info-procesos-subcategory-item">
                  <div className="info-procesos-subcategory-name">
                    {categoryName}
                  </div>
                  <div className="info-procesos-subcategory-value">
                    <span className="currency-symbol">$</span>
                    <span className="value-text">{formatNumber(category.valor)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Estados de carga y error
  if (loading) {
    return (
      <div className="info-procesos-container">
        <div className="info-procesos-loading">
          <div className="loading-spinner">
            <img src="/logocabeza.svg" alt="Logo" />
          </div>
          <p>Cargando información financiera...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="info-procesos-container">
        <div className="info-procesos-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="info-procesos-container">
        <div className="info-procesos-empty">
          <span className="empty-icon">📭</span>
          <p>No hay datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="info-procesos-container">
      <div className="info-procesos-grid">
        {GROUP_ORDER.map(groupName => {
          if (data.grupos[groupName] !== undefined) {
            return renderGroup(groupName, data.grupos[groupName]);
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default InfoProcesos;

